import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type OnChangeFn
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3 } from "lucide-react";
import { useState } from "react";
import type { DataTableColumnDef, DataTableColumnVisibility, DataTableSortingState } from "./tableTypes";

type DataTableProps<TData> = {
  className?: string;
  columnLabels?: Record<string, string>;
  columnVisibility: DataTableColumnVisibility;
  columns: Array<DataTableColumnDef<TData>>;
  data: TData[];
  emptyLabel: string;
  getRowId?: (row: TData, index: number) => string;
  initialSorting?: DataTableSortingState;
  labels: {
    columns: string;
    rows: (count: number) => string;
  };
  onColumnVisibilityChange: OnChangeFn<DataTableColumnVisibility>;
};

export function DataTable<TData>({
  className,
  columnLabels = {},
  columnVisibility,
  columns,
  data,
  emptyLabel,
  getRowId,
  initialSorting = [],
  labels,
  onColumnVisibilityChange
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<DataTableSortingState>(initialSorting);
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange,
    onSortingChange: setSorting,
    ...(getRowId ? { getRowId } : {}),
    state: {
      columnVisibility,
      sorting
    }
  });
  const tableClassName = className ? `data-table-shell ${className}` : "data-table-shell";
  const rows = table.getRowModel().rows;

  return (
    <div className={tableClassName}>
      <div className="data-table-toolbar">
        <span>{labels.rows(rows.length)}</span>
        <details className="data-table-columns">
          <summary>
            <Columns3 size={15} />
            <span>{labels.columns}</span>
          </summary>
          <div className="data-table-column-options">
            {table
              .getAllLeafColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <label key={column.id}>
                  <input
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                    type="checkbox"
                  />
                  <span>{columnLabels[column.id] ?? column.id}</span>
                </label>
              ))}
          </div>
        </details>
      </div>

      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortState = header.column.getIsSorted();
                  return (
                    <th
                      aria-sort={sortState === "asc" ? "ascending" : sortState === "desc" ? "descending" : "none"}
                      key={header.id}
                      scope="col"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button className="data-table-sort" onClick={header.column.getToggleSortingHandler()} type="button">
                          <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                          {sortState === "asc" ? (
                            <ArrowUp size={13} />
                          ) : sortState === "desc" ? (
                            <ArrowDown size={13} />
                          ) : (
                            <ChevronsUpDown size={13} />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="data-table-empty" colSpan={table.getVisibleLeafColumns().length}>
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
